import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { CheckCircle2, Shield, ArrowRight, Phone, Calendar, Clock, Loader2 } from 'lucide-react';

const BookDemoPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    practiceName: '',
    providers: '',
    role: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSelectChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email || 
        !formData.phone || !formData.practiceName || !formData.providers || !formData.role) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const apiUrl = process.env.REACT_APP_API_URL || (isLocalhost ? 'http://localhost:8001' : '');
      // Save to database
      const response = await fetch(`${apiUrl}/api/demo-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      // Navigate to schedule page on success
      navigate('/schedule-demo');
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your request. Please try again.');
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      title: "Reduce Costs",
      description: "Cut front office costs by up to 60%",
      icon: <TrendingDownIcon className="w-4 h-4 text-blue-600" />
    },
    {
      title: "24/7 Availability",
      description: "Answer 100% of calls, day or night",
      icon: <Clock className="w-4 h-4 text-blue-600" />
    },
    {
      title: "Smart Scheduling",
      description: "Fill schedule gaps automatically",
      icon: <Calendar className="w-4 h-4 text-blue-600" />
    },
    {
      title: "Instant Verification",
      description: "Verify insurance in real-time",
      icon: <Shield className="w-4 h-4 text-blue-600" />
    }
  ];

  return (
    <div className="min-h-screen bg-white selection:bg-blue-100 selection:text-blue-900">
      <Header />
      
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
      `}</style>

      <main className="pt-24 pb-12 px-4 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50 via-white to-indigo-50/50"></div>
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
          <div className="absolute top-0 -left-4 w-[500px] h-[500px] bg-purple-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-[500px] h-[500px] bg-indigo-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start justify-center">
            
            {/* Left Column: Value Prop */}
            <div className="flex-1 lg:pt-4 lg:sticky lg:top-28">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight text-slate-900 tracking-tight">
                See Asha's Dental AI <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  in Action
                </span>
              </h1>
              
              <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-lg">
                Join forward-thinking dental practices using AI to automate operations, delight patients, and grow revenue effortlessly.
              </p>

              {/* Benefits Grid */}
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex gap-3 p-3 rounded-xl bg-white/60 border border-white/60 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center border border-blue-100">
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm mb-0.5">{benefit.title}</h3>
                      <p className="text-xs text-slate-500 leading-snug">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>

            </div>

            {/* Right Column: Form */}
            <div className="w-full lg:w-[540px] flex-shrink-0 relative z-10">
              <Card className="shadow-2xl border-0 ring-1 ring-slate-900/5 bg-white/90 backdrop-blur-sm overflow-hidden">
                <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600"></div>
                <CardHeader className="pb-4 pt-6 px-6">
                  <CardTitle className="text-xl font-bold text-slate-900">
                    Get your personalized demo
                  </CardTitle>
                  <CardDescription className="text-slate-500 text-sm mt-1">
                    Fill out the form below to see how Asha can help.
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-6 pb-6">
                  <form className="space-y-3" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="firstName" className="text-xs font-semibold text-slate-700">First name *</Label>
                        <Input 
                          id="firstName" 
                          placeholder="Jane" 
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" 
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="lastName" className="text-xs font-semibold text-slate-700">Last name *</Label>
                        <Input 
                          id="lastName" 
                          placeholder="Doe"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" 
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-xs font-semibold text-slate-700">Work email *</Label>
                      <Input 
                        id="email" 
                        type="email" 
                        placeholder="jane@practice.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" 
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="phone" className="text-xs font-semibold text-slate-700">Phone number *</Label>
                      <Input 
                        id="phone" 
                        type="tel" 
                        placeholder="(555) 000-0000"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" 
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="practiceName" className="text-xs font-semibold text-slate-700">Practice name *</Label>
                      <Input 
                        id="practiceName" 
                        placeholder="Bright Smile Dental"
                        value={formData.practiceName}
                        onChange={handleInputChange}
                        required
                        className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" 
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="providers" className="text-xs font-semibold text-slate-700">Providers *</Label>
                        <Select onValueChange={(value) => handleSelectChange('providers', value)} required>
                          <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-2">1-2</SelectItem>
                            <SelectItem value="3-5">3-5</SelectItem>
                            <SelectItem value="6-10">6-10</SelectItem>
                            <SelectItem value="11+">11+</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="role" className="text-xs font-semibold text-slate-700">Role *</Label>
                        <Select onValueChange={(value) => handleSelectChange('role', value)} required>
                          <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-9 text-sm">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="owner">Owner/Dentist</SelectItem>
                            <SelectItem value="manager">Office Manager</SelectItem>
                            <SelectItem value="front-desk">Front Desk</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="message" className="text-xs font-semibold text-slate-700">Anything else? <span className="text-slate-400 font-normal">(Optional)</span></Label>
                      <Textarea 
                        id="message" 
                        placeholder="Tell us about your current challenges..."
                        value={formData.message}
                        onChange={handleInputChange}
                        className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[60px] resize-none transition-all text-sm"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10 text-base font-semibold rounded-lg shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Book Demo
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </>
                      )}
                    </Button>
                    
                    <p className="text-[10px] text-center text-slate-400 mt-3">
                      No commitment required. We'll show you exactly how it works.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

// Simple Icon Component for the benefits list
const TrendingDownIcon = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
    <polyline points="17 18 23 18 23 12" />
  </svg>
)

export default BookDemoPage;
